import { createContext, useContext, useEffect, useMemo, useReducer, useState } from "react"

import { getItemFromLocalStorage, setItemToLocalStorage } from "utils/localStorage"
import history from "utils/history"

import useGetUser from "api/querries/user/useGetUser"
import useGetLaundry from "api/querries/laundry/useGetLaundry"
import useGetLaundryBranches from "api/querries/laundry/useGetLaundryBranches"
import useGetLaundryHolidays from "api/querries/laundry/useGetLaundryHolidays"
import useGetAdminLaundryHolidays from "api/querries/laundry/useGetAdminLaundryHolidays"
import useAddLaundryHolidayMutation from "api/mutations/laundry/useAddLaundryHoliday"
import useDeleteLaundryHolidayMutation from "api/mutations/laundry/useDeleteLaundryHoliday"
import useUpdateLaundryHolidayMutation from "api/mutations/laundry/useUpdateLaundryHoliday"

import { APP_INITIAL_CONTEXT, APP_INITIAL_STATE } from "./AppProviderConstants"
import { IAppInitialContext, IAppProviderProps } from "./AppProviderTypes"
import { UI_INITIAL_CONTEXT, UI_INITIAL_STATE } from "providers/UIProvider/UIProviderConstants"
import { useUI } from "providers/UIProvider"
import { ILaundryHoliday, IAdminLaundryHoliday } from "types/laundry"
import useDebounce from "hooks/useDebounce"
import useAddAdminLaundryHolidayMutation from "api/mutations/laundry/useAddAdminLaundryHoliday"
import useUpdateReservationBoxMutation from "api/mutations/reservations/useUpdateReservationBox"
import useGetLaundryWithChangesCount from "api/querries/laundry/useGetLaundryWithChangesCount"
import { IRoles } from "types/shared"
import { NOTIFICATION_MESSAGE_TYPES } from "types/notification"
import { useIndexedDB } from "messageDB"
import { onMessageListener } from "../../firebase"


export const AppContext = createContext<IAppInitialContext>(APP_INITIAL_CONTEXT)

export const useAppContext: () => IAppInitialContext = () => useContext(AppContext)

const AppProvider: React.FC<IAppProviderProps> = ({ children }) => {
  const token = getItemFromLocalStorage("token");
  const { toggleModal, modals, lang } = useUI();
  const [state, setState] = useReducer(
    (oldState: any, newState: any) => ({ ...oldState, ...newState }),
    APP_INITIAL_STATE,
  )
  const [branchesSearchValue, setBranchesSearchValue] = useState("")
  const debouncedBranchesSearchValue = useDebounce(branchesSearchValue, 1000)
  const [branchesQuery, setBranchesQuery] = useState({
    pageSize: 100,
    pageIndex: 1,
    sortBy: "id",
    sortOrder: 1,
    search: ""
  })

  const { data: userData, isFetched: userDataFetched } = useGetUser({ enabled: Boolean(state.isAuthenticated && state.user === null), isAuthenticated: state.isAuthenticated })
  const { data: laundryData, isFetched: isLaundryDataFetched } = useGetLaundry({ enabled: Boolean(!state.isAdmin && state.isAuthenticated) })

  const isQappUser = state.userRole && [IRoles.QAPP_ADMIN, IRoles.QAPP_MANAGER].includes(state.userRole); // REAL BOOLEAN
  const { data: laundryBranchesData, isFetched: isLaundryBranchesDataFetched, isLoading: laundryBranchesDataLoading, refetch: refetchLaundryBranches } = useGetLaundryBranches(
    { ...branchesQuery },
    Boolean(state.isAuthenticated && isQappUser)
  )

  const { data: laundryHolidaysData, isFetched: isLaundryHolidaysDataFetched, refetch: refetchLaundryHolidays } = useGetLaundryHolidays({
    query: {
      laundryId: laundryData?.id,
      pageSize: 10,
      pageIndex: state.holidaysCurrentPage,
      sortBy: "id"
    },
    enabled: Boolean(state.isAuthenticated && !state.isAdmin && state.userRole && [IRoles.WASHER_ADMIN].includes(state.userRole))
  })

  const { data: AdminLaundryHolidaysData, isFetched: isAdminLaundryHolidaysDataFetched, refetch: refetchAdminLaundryHolidays } = useGetAdminLaundryHolidays(
    {
      carWashIds: state.selectedBranchesForHolidays,
      pageSize: 100,
      pageIndex: state.holidaysCurrentPage,
      sortBy: "id",
    }, Boolean(state.isAuthenticated && state.user?.roles[0] === IRoles.QAPP_ADMIN))

  const { data: laundryChangesCount, isFetched: laundryChangesCountFetched } = useGetLaundryWithChangesCount(Boolean(state.isAuthenticated && isQappUser));

  const { mutate: addLaundryHoliday, isSuccess: addLaundryHolidaySuccess } = useAddLaundryHolidayMutation()
  const { mutate: deleteLaundryHoliday, isSuccess: deleteLaundryHolidaySuccess } = useDeleteLaundryHolidayMutation()
  const { mutate: updateLaundryHoliday, isSuccess: updateLaundryHolidaySuccess } = useUpdateLaundryHolidayMutation()
  const { mutate: addAdminLaundryHoliday, isSuccess: addAdminLaundryHolidaySuccess } = useAddAdminLaundryHolidayMutation()

  const updateSelectedBranches = (newBranches: number[]) => setState({ selectedBranches: newBranches })
  const updateSelectedBranchesForHolidays = (newBranches: number[]) => setState({ selectedBranchesForHolidays: newBranches })
  const { mutate: updateReservationBox, isLoading: updateReservationBoxLoading, isSuccess: updateReservationBoxSuccess } = useUpdateReservationBoxMutation()
  const updateBranchesSearchValue = (val: string) => setBranchesSearchValue(val);


  useEffect(() => {
    const mainChannel = new BroadcastChannel("main-channel");
    mainChannel.postMessage(lang);
  }, [lang])

  function retrieveMessages() {
    return new Promise(function (resolve, reject) {
      useIndexedDB().createOrGetIndexedDB()
      const request = indexedDB.open('qapp-firebase-messages', 1);
      request.onsuccess = (event: any) => {
        const db = event.target.result;
        const tx = db.transaction('messages', 'readwrite');
        const store = tx.objectStore('messages');
        const messages: number[] = [];

        store.openCursor().onsuccess = async function (event: any) {
          const cursor = event.target.result;
          if (cursor) {
            const copy: any = { ...cursor.value }
            messages.push(copy);
            // await cursor.delete();
            cursor.continue();
          } else {
            resolve(messages as any[]);
          }
        };
      };
    });
  };

  async function onForeground() {
    const messages: any = await retrieveMessages();
    const reservationIds: number[] = messages.filter((e: any) => e.MessageType === NOTIFICATION_MESSAGE_TYPES.CAR_ON_PLACE).map((e: any) => parseInt(e.ReservationId))
    // if (reservationIds.length === 0) return;   // THIS SHOULD NOT BE COMMENTED!
    setState({ reservationIdsToUpdate: reservationIds })
  };

  onMessageListener().then(async payload => {

    onForeground()
  })

  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        const messages: any = await retrieveMessages();
        const reservations: number[] = messages.filter((e: any) => e.MessageType === NOTIFICATION_MESSAGE_TYPES.CAR_ON_PLACE)
        const reservationIds: number[] = reservations.map((reservation: any) => Number(reservation.ReservationId))
        if (reservationIds.length === 0) return;
        setState({ reservationIdsToUpdate: reservationIds })
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      // Clean up the event listener when the component unmounts.
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };

  }, []);

  useEffect(() => {
    if (state?.userRoles === IRoles.CASHIER) {
      return
    }

    if (state.reservationIdsToUpdate && state.verifiedBoxAssignInfo?.data?.length > 0) {
      toggleModal("carOnPlaceModal", true, {
        onDeny: () => {
          setState({ reservationIdsToUpdate: null, boxAssignInfo: null })
        },
        onSubmit: (reservationId: number, box: number) => updateReservationBox({ reservationId, box })
      })
    }
  }, [state.reservationIdsToUpdate, state.verifiedBoxAssignInfo?.data])


  useEffect(() => {
    if (userData?.status === 500) {
      setItemToLocalStorage("token", "")
      history.push("/login")
    }

    let updatedState = { ...state }

    if (userData && userDataFetched) {
      updatedState = {
        ...updatedState,
        isAdmin: userData?.isQappAdmin,
        userRole: userData?.roles[0],
        userName: userData?.userName
      }
      if (userData?.roles[0] !== IRoles.WASHER_MANAGER) {
        updatedState = {
          ...updatedState,
          user: userData
        }
      }

      if (userData?.roles[0] !== IRoles.CASHIER) {
        updatedState = {
          ...updatedState,
          user: userData
        }
      }
    }



    setState(updatedState)
  }, [userData])

  useEffect(() => {
    if (laundryData && isLaundryDataFetched) {
      setState({ laundry: laundryData.data })
    }
  }, [laundryData])





  useEffect(() => {
    if (laundryBranchesData && isLaundryBranchesDataFetched) {
      setState({ branches: laundryBranchesData.data })
    }
  }, [laundryBranchesData])

  useEffect(() => {
    if (laundryHolidaysData && isLaundryHolidaysDataFetched) {
      setState({ holidays: laundryHolidaysData.data })
    }
  }, [laundryHolidaysData])


  useEffect(() => {
    if (addLaundryHolidaySuccess) {
      toggleModal("addEditHolidayModal", false)
      refetchLaundryHolidays()
    }
  }, [addLaundryHolidaySuccess])

  useEffect(() => {
    if (AdminLaundryHolidaysData && isAdminLaundryHolidaysDataFetched)
      setState({ adminHolidays: AdminLaundryHolidaysData.data })
  }, [AdminLaundryHolidaysData])

  useEffect(() => {
    if (addAdminLaundryHolidaySuccess) {
      toggleModal("addAdminEditHolidayModal", false)
      refetchAdminLaundryHolidays()
    }
  }, [addAdminLaundryHolidaySuccess])

  useEffect(() => {
    if (deleteLaundryHolidaySuccess) {
      toggleModal("confirmationModal", false, UI_INITIAL_STATE.modals.confirmationModal.data)
      refetchLaundryHolidays()
      if (modals.holidaysModalForQappAdmin.isOpened) {
        refetchAdminLaundryHolidays()
      }
    }
  }, [deleteLaundryHolidaySuccess])

  useEffect(() => {
    if (updateLaundryHolidaySuccess) {
      toggleModal("addEditHolidayModal", false)
      refetchLaundryHolidays()
      if (modals.addAdminEditHolidayModal.isOpened) {
        toggleModal("addAdminEditHolidayModal", false)
        refetchAdminLaundryHolidays()
      }
    }
  }, [updateLaundryHolidaySuccess])

  useEffect(() => {
    if (branchesQuery && isLaundryBranchesDataFetched) {
      refetchLaundryBranches()
    }
  }, [branchesQuery])

  useEffect(() => {
    if (debouncedBranchesSearchValue !== undefined) {
      setBranchesQuery({
        ...branchesQuery,
        search: debouncedBranchesSearchValue
      })
    }
  }, [debouncedBranchesSearchValue])


  useEffect(() => {
    if (updateReservationBoxSuccess) {
      setState({ reservationIdsToUpdate: null, boxAssignInfo: null })
      toggleModal("carOnPlaceModal", false, UI_INITIAL_CONTEXT.modals.carOnPlaceModal.data)
    }
  }, [updateReservationBoxSuccess])

  useEffect(() => {
    if (laundryChangesCount && laundryChangesCountFetched) {
      setState({ laundryChangesCount: laundryChangesCount.data })
    }
  }, [laundryChangesCount, laundryChangesCountFetched])

  useEffect(() => {
    if (token) {
      setState({ isAuthenticated: true })
    } else {
      setState({ isAuthenticated: false })
    }
  }, [token])




  // function retrieveMessages() {
  //   return new Promise(function (resolve, reject) {
  //     useIndexedDB().createOrGetIndexedDB()
  //     const request = indexedDB.open('qapp-firebase-messages', 1);
  //     request.onsuccess = (event: any) => {
  //       const db = event.target.result;
  //       const tx = db.transaction('messages', 'readwrite');
  //       const store = tx.objectStore('messages');
  //       const messages: number[] = [];
  //       store.openCursor().onsuccess = async function (event: any) {
  //         const cursor = event.target.result;
  //         if (cursor) {
  //           const copy: any = { ...cursor.value }
  //           messages.push(copy);
  //           await cursor.delete();
  //           cursor.continue();
  //         } else {
  //           resolve(messages as any[]);
  //         }
  //       };
  //     };
  //   });
  // }



  // self.addEventListener("visibilitychange", () => onFocus())

  // async function onFocus() {
  //   console.log("went in appProvider")
  //   const messages: any = await retrieveMessages();
  //   const reservationIds: number[] = messages.filter((e: any) => e.MessageType === NOTIFICATION_MESSAGE_TYPES.CAR_ON_PLACE).map((e: any) => parseInt(e.ReservationId))
  //   if (reservationIds.length === 0) return;
  //   setState({ reservationIdsToUpdate: reservationIds })
  // }

  const handleLaundryHolidaysPageChange = () => {
    setState({ holidaysCurrentPage: state.currentPage + 1 })
  }

  const setCurrentHoliday = (holiday: ILaundryHoliday) => setState({ currentHoliday: holiday })
  const setAdminCurrentHoliday = (adminHoliday: IAdminLaundryHoliday) => setState({ adminCurrentHoliday: adminHoliday })

  const value = useMemo(() => ({
    ...state,
    addLaundryHoliday,
    addAdminLaundryHoliday,
    updateLaundryHoliday,
    deleteLaundryHoliday,
    setState,
    handleLaundryHolidaysPageChange,
    setCurrentHoliday,
    setAdminCurrentHoliday,
    updateReservationBox,
    laundryBranchesDataLoading,
    updateSelectedBranches,
    updateSelectedBranchesForHolidays,
    refetchLaundryBranches,
    refetchAdminLaundryHolidays,
    branchesSearchValue,
    updateBranchesSearchValue
  }), [state, addLaundryHolidaySuccess, addAdminLaundryHolidaySuccess, laundryBranchesDataLoading, updateLaundryHolidaySuccess, deleteLaundryHolidaySuccess, branchesSearchValue])

  return <AppContext.Provider value={value}>
    {children}
  </AppContext.Provider>
}

export default AppProvider